// Ported from dev-scrips-pm-react src/billing/services/use-invoice.ts (PROD-3312 appointment Billing tab).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { showSuccess } from '@/shared/lib/toast-helpers';
import { invalidateBalanceViews } from '@/billing/services/invalidate-balance-views';
import type {
  CancellationReason,
  InvoiceDetail,
  IssueAndAllocateRequest,
  ResubmissionType,
} from '@/shared/types/billing';

/**
 * Single-invoice detail + cancel action.
 *
 * Wire endpoints (mirror Flutter `billing_data_source_impl.dart`):
 *
 *   GET /Billing/Invoice/{invoiceId}?resubmissionType=<value>  — single invoice detail
 *   PUT /Billing/Invoice/CancelInvoice/{invoiceId}             body: { reasonId } — cancel
 *
 * The `resubmissionType` query param is OPTIONAL. Used by the Claim
 * Review popup's resubmission flow (`cameFrom === 'resubmission'` per
 * Flutter `review_claim_popup.dart:173-179`). The popup passes the
 * current `resubmissionType` so the backend can populate
 * resubmission-specific fields on the returned invoice. When absent,
 * the GET behaves identically to the no-param case (same schema).
 *
 * **Query param casing**: swagger declares the param as `resubmissionType`
 * (camelCase). Flutter sends `ResubmissionType` (PascalCase). .NET model
 * binder is case-insensitive so both work; we follow swagger (the
 * documented contract).
 *
 * **Tenant scoping**: the api-client at `src/shared/api/api-client.ts:50-51`
 * injects `OrganizationId` and `PracticeId` headers on every request from
 * the authenticated user context. The backend uses those headers to
 * filter the invoice by tenant before returning — a user manipulating
 * the route param to probe a foreign tenant's invoice id will 404 (or
 * 403) at the .NET model binder. No invoice-id-only cross-tenant access
 * is possible without a valid tenant-bearing session.
 *
 * There is NO flat `GET /Invoices` list endpoint on dev (verified 2026-05-13:
 * 404 for `/Invoice`, `/Invoices`, `/Invoice/List`). Org-wide invoice
 * browsing happens via the Claim Workbench (PROD-698 — `POST /InvoiceClaim/Claims`).
 * Patient-scoped browsing lives in the Patient module.
 */

const KEY_ROOT = ['invoice'] as const;

export interface UseInvoiceParams {
  invoiceId: string | undefined;
  /**
   * Optional resubmission discriminator. Forwarded as `&resubmissionType=...`
   * when set. Required only for the Claim Review popup's resubmission flow.
   */
  resubmissionType?: ResubmissionType;
  /**
   * When true, ALWAYS hit the network on mount and never retain the cache once
   * the last observer unmounts. The global default `staleTime` is 5 minutes
   * (`app/providers/query-client.ts:93`), which means reopening the claim popup
   * within that window served a cached invoice snapshot instead of calling the
   * API — a real hazard for the claim-review surface, where the operator acts on
   * the numbers (coverage, Member ID, totals) and a stale snapshot can drive a
   * wrong submission (PROD-1825). The claim popup passes `true` so every open
   * reflects server truth. `staleTime: 0` + `refetchOnMount: 'always'` force the
   * GET; `gcTime: 0` drops the entry on close so nothing lingers to be re-served.
   */
  alwaysFresh?: boolean;
}

export function useInvoice({
  invoiceId,
  resubmissionType,
  alwaysFresh = false,
}: UseInvoiceParams) {
  return useQuery({
    queryKey: [...KEY_ROOT, 'detail', invoiceId, resubmissionType ?? null],
    queryFn: ({ signal }) => {
      const qs = resubmissionType
        ? `?resubmissionType=${encodeURIComponent(resubmissionType)}`
        : '';
      return get<InvoiceDetail>(
        `${BILLING_BASE}/Invoice/${invoiceId}${qs}`,
        { signal },
      );
    },
    enabled: Boolean(invoiceId),
    ...(alwaysFresh
      ? { staleTime: 0, gcTime: 0, refetchOnMount: 'always' as const }
      : {}),
  });
}

/**
 * Cancel an invoice with a reason ID. Flutter `cancelInvoice` parity —
 * note the body uses `reasonId` (not `reason` or `cancellationReason`).
 * Backend looks up the cancellation reason value-set on the server side.
 *
 * Invalidates this invoice's detail key and the workbench list root key
 * (so the row picks up the new `Cancelled` status on next refresh).
 */
export interface CancelInvoiceVars {
  invoiceId: string;
  reasonId: string;
}

export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, reasonId }: CancelInvoiceVars) =>
      put<InvoiceDetail>(
        `${BILLING_BASE}/Invoice/CancelInvoice/${invoiceId}`,
        { reasonId },
      ),
    onSuccess: (_data, vars) => {
      showSuccess('Invoice cancelled successfully');
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
      void qc.invalidateQueries({ queryKey: ['invoice', 'detail', vars.invoiceId] });
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      // Total Unpaid / balances (PROD-1809) + package grants (PROD-2126):
      // issue consumes package units, cancel reverses them.
      invalidateBalanceViews(qc);
    },
  });
}

/**
 * Issue & Allocate (Draft → Issued, creates the claim). Flutter
 * `issueAndAllocate` parity — `PUT /Billing/InvoiceClaim/{claimId}/Reissue`.
 * Body is `IssueAndAllocateRequest`; the endpoint returns a bare `true`.
 * Invalidates the open invoice + the workbench list.
 */
export interface IssueAndAllocateVars {
  claimId: string;
  body: IssueAndAllocateRequest;
}

export function useIssueAndAllocate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, body }: IssueAndAllocateVars) =>
      put<boolean>(`${BILLING_BASE}/InvoiceClaim/${claimId}/Reissue`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      // Total Unpaid / balances (PROD-1809) + package grants (PROD-2126):
      // issue consumes package units, cancel reverses them.
      invalidateBalanceViews(qc);
    },
  });
}

/**
 * Issue Invoice (Draft → Issued) for the appointment/patient billing tab —
 * Flutter `issueInvoice` (`billing_data_source_impl.dart:989`):
 *   `PUT /Billing/Invoice/IssueInvoice/{invoiceId}` body `{ invoiceDate, note }`.
 * `invoiceDate` ships as no-Z ISO (curl-verified 2026-06-04:
 * `"2026-06-04T00:17:01.000"`). Distinct from the claim `IssueAndAllocate`
 * (Reissue) above — this one issues a plain invoice, no claim creation.
 */
export interface IssueInvoiceVars {
  invoiceId: string;
  /** No-Z ISO (e.g. `2026-06-04T00:17:01.000`). */
  invoiceDate: string;
  /**
   * ⚠️ CONTRACT GAP (PROD-1760, review of PR #773): whether the backend treats
   * `note: null` as "leave the stored note alone" or "overwrite it with null" is
   * **unconfirmed**. It cannot be determined from either the Flutter or the React
   * source — there is no swagger and no contract artefact in either repo, and
   * Flutter's datasource serialises the field unconditionally so its behaviour
   * proves nothing either way. Closing this needs a dev curl or a read of the
   * `IssueInvoice` handler.
   *
   * The caller in `invoice-detail-page.tsx` is written to be safe under both
   * readings: an untouched field re-sends the stored note rather than null, so
   * nothing is lost if the backend does overwrite. Do not "simplify" that to
   * `null` before this gap is closed.
   */
  note?: string | null;
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, invoiceDate, note = null }: IssueInvoiceVars) =>
      put<InvoiceDetail>(`${BILLING_BASE}/Invoice/IssueInvoice/${invoiceId}`, {
        invoiceDate,
        note,
      }),
    // The issue dialog shows the server's reason inline; a toast on top would cover it.
    meta: { handledInline: true },
    onSuccess: (_data, vars) => {
      showSuccess('Invoice issued');
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
      void qc.invalidateQueries({ queryKey: ['invoice', 'detail', vars.invoiceId] });
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      // Total Unpaid / balances (PROD-1809) + package grants (PROD-2126):
      // issue consumes package units, cancel reverses them.
      invalidateBalanceViews(qc);
    },
  });
}

/**
 * Invoice cancellation reasons for the Cancel dialog dropdown. Flutter
 * `fetchCancellationReason` with `searchFor = reason_type_CANCELLATION`
 * (`common_data_source_impl.dart:854-861`) →
 * `GET /Billing/Invoice/GetInvoiceMasterData?type=Cancellation`.
 * Returns `{ id, value }[]`; the chosen `id` becomes the CancelInvoice
 * `reasonId`. Reasons rarely change — cache for the session.
 */
export function useCancellationReasons(enabled = true) {
  return useQuery({
    queryKey: ['cancellation-reasons'],
    queryFn: ({ signal }) =>
      get<CancellationReason[]>(
        `${BILLING_BASE}/Invoice/GetInvoiceMasterData?type=Cancellation`,
        { signal },
      ),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}
